import { DatabaseFieldType } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  createEntry,
  createField,
  getExistingFieldNames,
} from '@databases/utils/database/utils';
import { getHiddenFields } from '@databases/utils/database/view/view';
import { getUniqueFieldName } from '@databases/utils/field-name';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch } from '@databases/utils/redux/redux';
import { generateId } from '@databases/utils/utils';
import { type AnyAction } from 'redux';
import { type ThunkAction } from 'redux-thunk';

import { type DefaultRootState } from '../..';
import { deselectAll, selectColumns, selectEntries } from '../cellSelections/actions';
import {
  selectSelectedEntireEntryIds,
  selectSelectedEntireFieldIds,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '../cellSelections/selectors';
import { addChangeToDraft } from '../changesets/actions';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

export const doInsertEntries =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    insertBefore: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const entry = createEntry(database, viewSettings);

    const selectedEntryIds = selectSelectedEntryIds(getState(), {
      contentId,
    });
    if (selectedEntryIds.length === 0) {
      return;
    }

    const aboveOrBelowSelectedEntry = insertBefore
      ? selectedEntryIds[0]
      : selectedEntryIds[selectedEntryIds.length - 1];

    const index = database.entries.indexOf(aboveOrBelowSelectedEntry);
    dispatch(
      addChangeToDraft(contentId, {
        type: DatabaseChangeType.ADD_ENTRY,
        entry,
        index: insertBefore ? index : index + 1,
      })
    );
    dispatch(deselectAll());
  };

export const doDuplicateEntireEntries =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    dispatch(
      addChangeToDraft(contentId, {
        type: DatabaseChangeType.CLONE_ENTRIES,
        entryIds: Object.fromEntries(
          selectSelectedEntireEntryIds(getState(), {
            contentId,
          }).map((entryId) => [entryId, generateId()])
        ),
      })
    );
  };

export const doClearEntireEntries =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntireEntryIds = selectSelectedEntireEntryIds(getState(), {
      contentId,
    });
    dispatch(
      addChangeToDraft(contentId, {
        type: DatabaseChangeType.CLEAR_ENTRIES,
        entryIds: selectedEntireEntryIds,
      })
    );
  };

export const doDeleteEntries =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntryIds = selectSelectedEntryIds(getState(), {
      contentId,
    });
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        selectedEntryIds.forEach((entryId) => {
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.DELETE_ENTRY,
              entryId: entryId,
            })
          );
        });
      });
    });
  };

export const doSelectNewEntries =
  (contentId: string, callback: () => Promise<void> | void): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    await callback();
    const newDatabase = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const newEntryIds = newDatabase.entries.filter(
      (id) => !database.entries.includes(id)
    );
    if (newEntryIds.length > 0) {
      batch(() => {
        dispatch(
          selectEntries({
            contentId,
            entryIds: newEntryIds,
          })
        );
      });
    }
  };

export const doMoveEntries =
  (contentId: string, moveUp: boolean): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntryIds = selectSelectedEntryIds(getState(), {
      contentId,
    });
    const databaseDraft = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const firstOrLastEntryId = moveUp
      ? selectedEntryIds[0]
      : selectedEntryIds[selectedEntryIds.length - 1];
    const currentIndexInEntryIds = databaseDraft.entries.indexOf(firstOrLastEntryId);
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.REORDER_ENTRIES,
            entryIds: selectedEntryIds,
            newIndex: moveUp ? currentIndexInEntryIds - 1 : currentIndexInEntryIds + 2,
          })
        );
      });
    });
  };

export const doInsertFields =
  (
    contentId: string,
    currentViewId: string,
    insertBefore: boolean,
    fieldName: string
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const newField = createField(DatabaseFieldType.TEXT);
    const uniqueFieldName = getUniqueFieldName(
      fieldName,
      getExistingFieldNames(database, newField)
    );

    const selectedFieldIds = selectSelectedFieldIds(getState(), {
      contentId,
    });
    if (selectedFieldIds.length === 0) {
      return;
    }

    const beforeOrAfterSelectedField = insertBefore
      ? selectedFieldIds[0]
      : selectedFieldIds[selectedFieldIds.length - 1];

    const index = database.fields.indexOf(beforeOrAfterSelectedField);
    dispatch(
      addChangeToDraft(contentId, {
        type: DatabaseChangeType.ADD_FIELD,
        field: { ...newField, name: uniqueFieldName },
        index: insertBefore ? index : index + 1,
        currentViewId,
      })
    );
    dispatch(deselectAll());
  };

export const doDuplicateEntireFields =
  (contentId: string, currentViewId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntireFieldIds = selectSelectedEntireFieldIds(getState(), {
      contentId,
    });
    dispatch(
      addChangeToDraft(contentId, {
        type: DatabaseChangeType.CLONE_FIELDS,
        fieldIds: Object.fromEntries(
          selectedEntireFieldIds.map((fieldId) => [fieldId, generateId()])
        ),
        currentViewId,
      })
    );
  };

export const doClearEntireFields =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntireFieldIds = selectSelectedEntireFieldIds(getState(), {
      contentId,
    });
    dispatch(
      addChangeToDraft(contentId, {
        type: DatabaseChangeType.CLEAR_ENTRIES,
        fieldIds: selectedEntireFieldIds,
      })
    );
  };

export const doDeleteFields =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedFieldIds = selectSelectedFieldIds(getState(), {
      contentId,
    });
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        selectedFieldIds.forEach((fieldId) => {
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.DELETE_FIELD,
              fieldId,
            })
          );
        });
      });
    });
  };

export const doMoveFields =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined,
    moveLeft: boolean
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedFieldIds = selectSelectedFieldIds(getState(), {
      contentId,
    });
    const databaseView = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const hiddenFields = getHiddenFields(viewSettings?.fields);

    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        selectedFieldIds.forEach((fieldId, idx) => {
          const currentIndexInFields = databaseView.fields.indexOf(fieldId);
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.REORDER_FIELD,
              fieldId,
              hiddenFields,
              newIndex: moveLeft
                ? currentIndexInFields - 1
                : currentIndexInFields + selectedFieldIds.length - idx + 1,
            })
          );
        });
      });
    });
  };

export const doSelectNewFields =
  (contentId: string, callback: () => Promise<void> | void): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    await callback();
    const newDatabase = selectDatabaseByContentIdRequired(getState(), {
      contentId,
    });
    const newFieldIds = newDatabase.fields.filter((id) => !database.fields.includes(id));
    if (newFieldIds.length > 0) {
      batch(() => {
        dispatch(
          selectColumns({
            contentId,
            fieldIds: newFieldIds,
          })
        );
      });
    }
  };

export const doClearValues =
  (contentId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedEntryIds = selectSelectedEntryIds(getState(), {
      contentId,
    });
    const selectedFieldIds = selectSelectedFieldIds(getState(), {
      contentId,
    });
    dispatch(
      addChangeToDraft(contentId, {
        type: DatabaseChangeType.CLEAR_ENTRIES,
        entryIds: selectedEntryIds,
        fieldIds: selectedFieldIds,
      })
    );
  };
